import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { TransferTicketRequest } from '@packages/types/requestTypes';
import { getOrderQuery, getTicketQuery, QUERY_KEYS } from '@/api/queries';
import { FirebaseClient } from '@/api/firebaseClient';
import { useInvalidatingMutation } from '@/hooks/useInvalidatingMutation';
import { getAdminCheckinUrl } from '@/features/ticketing/adminCheckinUrl';
import {
  Container,
  QR,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Input,
  Label,
} from '@/components';

const client = new FirebaseClient();

export const TicketDetails = () => {
  const { t } = useTranslation();
  const { ticketId } = useParams<{ ticketId: string }>();
  const { data: ticket } = useQuery(getTicketQuery(ticketId ?? ''));
  const { data: order } = useQuery(getOrderQuery(ticket?.orderId ?? ''));
  const isEventTicket = (ticket?.owner ?? order?.order.owner)?.kind === 'event';

  const [showTransfer, setShowTransfer] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [transferred, setTransferred] = useState(false);

  const { mutateAsync: transferTicketFn } = useInvalidatingMutation([QUERY_KEYS.TICKETS], {
    mutationFn: async (data: TransferTicketRequest) => client.transferTicket(data),
  });

  const submitTransfer = async () => {
    const form = document.getElementById('ticketTransferForm') as HTMLFormElement | null;
    if (!form?.reportValidity() || !ticket) return;
    try {
      await transferTicketFn({ name, email, ticketId: ticket.ticketId, orderId: ticket.orderId });
      setName('');
      setEmail('');
      setShowTransfer(false);
      setTransferred(true);
    } catch (e) {
      console.error(t('errorBuyingTicket'), e);
    }
  };

  if (!ticket || !order) return null;

  if (ticket.canceledAt) {
    return (
      <Container max={760}>
        <h1 className="font-disp text-5xl uppercase text-ww-charcoal">{t('ticketCanceled')}</h1>
        <div
          className="mt-6 p-6"
          style={{
            background: 'var(--paper-50)',
            border: '2px solid var(--ww-orange)',
            boxShadow: '5px 5px 0 var(--ww-orange)',
          }}>
          <h2 className="font-sans text-xl font-bold text-ww-orange">{t('ticketCanceled')}</h2>
          <p className="mt-2 font-sans text-ink-soft">{t('ticketCanceled_message')}</p>
          <dl className="mt-4 font-mono text-sm">
            <dt className="text-ink-soft">{t('ticketNumber')}</dt>
            <dd className="mb-2">{ticket.ticketId}</dd>
            <dt className="text-ink-soft">E-Mail</dt>
            <dd>{ticket.email}</dd>
          </dl>
        </div>
      </Container>
    );
  }

  const paid = Boolean(order.order.paidAt);
  const ticketOwner = ticket.owner ?? order.order.owner;
  const checkinUrl = getAdminCheckinUrl(ticketOwner, ticket.ticketId);
  const meta: [string, string][] = [
    [t('ticket'), ticket.ticketType.label],
    [t('price'), `${ticket.price} €`],
    [t('name'), ticket.name],
    [t('email'), ticket.email],
    [t('orderNumber'), ticket.orderId],
  ];

  return (
    <Container max={920}>
      <div className="ww-eyebrow" style={{ color: 'var(--ww-orange)' }}>
        {t('ticketStatus')}
      </div>
      <h1 className="mb-7 mt-2 font-disp text-5xl uppercase text-ww-charcoal">
        {t('your_ticket')}
      </h1>

      <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-[1fr_1.25fr]">
        {/* QR ticket stub */}
        <div
          className="ww-grain ww-grain--soft relative"
          style={{
            background: 'var(--paper-50)',
            border: '3px solid var(--ww-charcoal)',
            boxShadow: 'var(--shadow-lift)',
            padding: 26,
            transform: 'rotate(-1.4deg)',
          }}>
          {paid ? (
            <>
              <div className="flex justify-center">
                <QR size={210} value={checkinUrl} />
              </div>
              <div className="mt-4 text-center font-mono text-[15px] tracking-[0.12em] text-ww-charcoal">
                {ticket.ticketId}
              </div>
              <div
                className="text-center font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft"
                style={{
                  borderTop: '2px dashed var(--ww-charcoal)',
                  margin: '18px -26px 0',
                  paddingTop: 14,
                }}>
                {t('scan_at_entrance')}
              </div>
            </>
          ) : (
            <div className="py-6 text-center">
              <div className="font-disp text-3xl uppercase text-ww-charcoal">
                {t('qr_pending_title')}
              </div>
              <p className="mt-3 font-sans text-sm text-ink-soft">{t('qr_pending_message')}</p>
            </div>
          )}
        </div>

        {/* meta */}
        <div>
          {paid ? (
            <div
              className="mb-6 inline-flex items-center gap-2.5 px-3.5 py-2"
              style={{
                background: 'var(--ww-chartreuse)',
                border: '2px solid var(--ww-charcoal)',
              }}>
              <span className="font-mono text-[13px] font-semibold tracking-[0.06em] text-ww-charcoal">
                ✓ {t('paymentReceivedOn')}{' '}
                {Intl.DateTimeFormat('de-DE').format(new Date(order.order.paidAt!))}
              </span>
            </div>
          ) : (
            <div
              className="mb-6 flex flex-wrap items-center justify-between gap-4 p-4"
              style={{
                background: 'var(--ww-orange)',
                color: 'var(--paper-50)',
                border: '2px solid var(--ww-charcoal)',
              }}>
              <span className="font-sans font-bold">{t('paymentPending_message')}</span>
              <Link
                to={`/orders/${ticket.orderId}`}
                className="ww-btn"
                style={{ background: 'var(--paper-50)' }}>
                {t('payNow')} →
              </Link>
            </div>
          )}

          <dl className="m-0">
            {meta.map(([k, v]) => (
              <div
                key={k}
                className="flex justify-between gap-4 py-3"
                style={{ borderBottom: '1px solid var(--paper-300)' }}>
                <dt className="font-mono text-xs uppercase tracking-[0.08em] text-ink-soft">{k}</dt>
                <dd
                  className={`m-0 text-[17px] font-medium text-ww-charcoal ${
                    v === ticket.orderId ? 'font-mono' : 'font-sans'
                  }`}>
                  {v}
                </dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex flex-wrap gap-3.5">
            {paid && !transferred ? (
              <button
                type="button"
                className="ww-btn ww-btn--ink"
                onClick={() => setShowTransfer(true)}>
                {t('transfer_ticket')}
              </button>
            ) : null}
            <Link to={`/orders/${ticket.orderId}`} className="ww-btn ww-btn--ghost">
              {t('gotoOrder')}
            </Link>
            {!isEventTicket ? (
              <a
                href="https://docs.google.com/spreadsheets/d/1UD_orcE0ynoSDdVDrWL3hxMTHo7bfbVE3Gkq3sF6d-A/edit?gid=0#gid=0"
                target="_blank"
                rel="noreferrer"
                className="ww-btn ww-btn--ghost">
                {t('supportPlan')}
              </a>
            ) : null}
          </div>
          {transferred ? (
            <p className="mt-3 font-sans text-sm text-ww-charcoal">
              {t('ticket_transfer_successful')}
            </p>
          ) : null}
        </div>
      </div>

      <Dialog open={showTransfer} onOpenChange={setShowTransfer}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{t('transfer_ticket_title')}</DialogTitle>
            <DialogDescription>{t('transfer_ticket_description')}</DialogDescription>
          </DialogHeader>
          <form id="ticketTransferForm" className="flex w-full flex-col gap-3 py-2">
            <label className="block">
              <Label>{t('full_name')}</Label>
              <Input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('full_name')}
              />
            </label>
            <label className="block">
              <Label>E-Mail</Label>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@mail.de"
              />
            </label>
          </form>
          <DialogFooter>
            <button
              type="button"
              className="ww-btn ww-btn--ghost"
              onClick={() => setShowTransfer(false)}>
              {t('cancel')}
            </button>
            <button type="button" className="ww-btn ww-btn--primary" onClick={submitTransfer}>
              {t('transfer_ticket')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Container>
  );
};
